/**
 * FinancialEntryHub — iter-237.6 (Feb 2026)
 *
 * "الإدراج المالي للشركة" — 4 entry-only sub-cards:
 *   1) إدراج الرواتب        → /finance/asb606/payments/salaries?mode=entry
 *   2) إدراج المدفوعات      → /finance/asb606/payments/company?mode=entry
 *   3) إدراج أصول الشركة    → /finance/asb606/payments/assets?mode=entry
 *   4) إدراج مستحقات الشركة → /finance/asb606/payments/liabilities?mode=entry
 *
 * The sub-pages read `?mode=entry` and hide their records tables so the
 * operator lands directly on the input form.
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Briefcase, Gem, HandCoins,
  ArrowLeft, ArrowRight,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

export default function FinancialEntryHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const CARDS = [
    {
      id: 'salaries',
      icon: Users,
      color: '#10B981',
      route: '/finance/asb606/payments/salaries?mode=entry',
      titleAr: 'إدراج الرواتب',
      titleEn: 'Salaries Entry',
      descAr: 'إضافة دفعة راتب شهري لموظف — ينتقل مباشرةً إلى نموذج الإدخال',
      descEn: 'Add a monthly salary payment — opens the input form directly',
      testid: 'entry-hub-salaries',
    },
    {
      id: 'payments',
      icon: Briefcase,
      color: '#3B82F6',
      route: '/finance/asb606/payments/company?mode=entry',
      titleAr: 'إدراج المدفوعات',
      titleEn: 'Payments Entry',
      descAr: 'إضافة فاتورة/مدفوعة للاشتراكات والخدمات (فردية أو مجموعة)',
      descEn: 'Add subscription/service invoices (single or bulk)',
      testid: 'entry-hub-payments',
    },
    {
      id: 'assets',
      icon: Gem,
      color: '#d4af37',
      route: '/finance/asb606/payments/assets?mode=entry',
      titleAr: 'إدراج أصول الشركة',
      titleEn: 'Company Assets Entry',
      descAr: 'إدراج المعادن والمجوهرات، الأسهم، والعقارات',
      descEn: 'Metals & jewelry, stocks, and real estate',
      testid: 'entry-hub-assets',
    },
    {
      id: 'liabilities',
      icon: HandCoins,
      color: '#EF4444',
      route: '/finance/asb606/payments/liabilities?mode=entry',
      titleAr: 'إدراج مستحقات الشركة',
      titleEn: 'Liabilities Entry',
      descAr: 'إضافة مستحقات على الشركة أو تسديد مستحق موجود',
      descEn: 'Add a new liability or record a payment against an existing one',
      testid: 'entry-hub-liabilities',
    },
  ];

  return (
    <DashboardLayout
      title="الإدراج المالي للشركة"
      titleEn="Financial Entry"
      subtitle="أضف بيانات مالية جديدة (رواتب، مدفوعات، أصول، مستحقات)"
      subtitleEn="Add new financial data (salaries, payments, assets, liabilities)"
      screenCode="ASB-FIN-PAY-ENTRY"
      backPath="/finance/asb606"
      onBack={() => navigate('/finance/asb606')}
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'ASB606 (المالية)', labelEn: 'ASB606 (Finance)', path: '/finance/asb606' },
        { labelAr: 'الإدراج المالي', labelEn: 'Financial Entry' },
      ]}
    >
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
        data-testid="financial-entry-hub"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {visiblePages(CARDS, 'route').map((card, i) => {
            const Icon = card.icon;
            const Arrow = isAr ? ArrowLeft : ArrowRight;
            return (
              <motion.button
                key={card.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -4, scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => navigate(card.route)}
                className="group relative bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-6 text-start transition-all overflow-hidden"
                style={{ borderColor: `${card.color}30` }}
                data-testid={card.testid}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                    style={{ backgroundColor: `${card.color}15`, borderColor: `${card.color}40` }}
                  >
                    <Icon className="h-7 w-7" style={{ color: card.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                      {isAr ? card.titleAr : card.titleEn}
                      <Arrow className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: card.color }} />
                    </h3>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">
                      {isAr ? card.descAr : card.descEn}
                    </p>
                  </div>
                </div>
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ background: `radial-gradient(circle at 50% 50%, ${card.color}10 0%, transparent 70%)` }}
                />
              </motion.button>
            );
          })}
        </div>
      </section>
    </DashboardLayout>
  );
}
